// Captcha.js — Cloudflare Turnstile widget (anti-bot on public forms: signup, contact, Aira lead).
//
// Env-gated: renders NOTHING unless REACT_APP_TURNSTILE_SITE_KEY is set (the backend seam
// core/captcha.py likewise no-ops without TURNSTILE_SECRET_KEY), so dev/self-hosted installs work
// keyless and the e2e suites never see it. Tokens are single-use — call resetCaptcha() after every
// submit attempt that sent one. The script loads lazily, once, only when a widget actually mounts.
import React, { useEffect, useRef } from "react";

export const CAPTCHA_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();
export const captchaEnabled = !!CAPTCHA_SITE_KEY;

let scriptPromise = null;
function loadScript() {
  if (!captchaEnabled) return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/** Reset every rendered widget so a fresh token is minted (tokens are single-use). */
export function resetCaptcha() {
  try { window.turnstile?.reset(); } catch { /* widget not rendered */ }
}

export default function Captcha({ onToken, action, className }) {
  const hostRef = useRef(null);
  const widgetRef = useRef(null);

  useEffect(() => {
    if (!captchaEnabled) return undefined;
    let cancelled = false;
    loadScript().then((ts) => {
      if (cancelled || !ts || !hostRef.current || widgetRef.current != null) return;
      widgetRef.current = ts.render(hostRef.current, {
        sitekey: CAPTCHA_SITE_KEY,
        action,
        callback: (token) => onToken?.(token),
        "expired-callback": () => onToken?.(""),
        "error-callback": () => onToken?.(""),
      });
    }).catch(() => { /* script blocked/unreachable — backend fails closed, user sees its 400 */ });
    return () => {
      cancelled = true;
      try { if (widgetRef.current != null) window.turnstile?.remove(widgetRef.current); } catch { /* noop */ }
      widgetRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!captchaEnabled) return null;
  return <div ref={hostRef} className={className} data-testid="captcha-widget" />;
}
